var __jsx = React.createElement;
import React from "react";
import TextInput from "./TextInput";
import NumberInput from "./NumberInput";
import DateInput from "./DateInput";
import SelectInput from "./SelectInput";
import MultiSelectInput from "./MultiSelectInput";
import BadgeMultiSelectInput from "./BadgeMultiSelectInput";
import TagInput from "./TagInput";
import { isDatePattern, getAdjustedMaxLength, isNumericPattern } from "../../../utils/fieldValidation";
import { applyLCSRValidationRules } from "../../../utils/lcsrFieldValidationRules";
var FieldInputRenderer = function FieldInputRenderer(_ref) {
  var _metadata$pattern3;
  var field = _ref.field,
    value = _ref.value,
    onChange = _ref.onChange,
    onDropdownStateChange = _ref.onDropdownStateChange,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    _ref$isEmpty = _ref.isEmpty,
    isEmpty = _ref$isEmpty === void 0 ? false : _ref$isEmpty,
    _ref$isRequired = _ref.isRequired,
    isRequired = _ref$isRequired === void 0 ? false : _ref$isRequired,
    _ref$errorMessage = _ref.errorMessage,
    errorMessage = _ref$errorMessage === void 0 ? "" : _ref$errorMessage,
    _ref$isShouldBeBlankE = _ref.isShouldBeBlankError,
    isShouldBeBlankError = _ref$isShouldBeBlankE === void 0 ? false : _ref$isShouldBeBlankE;
  var metadata = field.metadata || {};

  // Map data_type to input type
  var getInputType = function getInputType() {
    var _metadata$valid_value, _metadata$pattern, _metadata$pattern2;
    var dataType = (metadata.data_type || "string").toLowerCase();

    // Priority 1: If field has valid_values, use select or multi-select
    if (metadata.valid_values && metadata.valid_values.length > 0) {
      return metadata.multi_select ? "badge-multi-select" : "select";
    }

    // Priority 2: Array types or multi_select without predefined values → tag input
    if (dataType.startsWith("array") || metadata.multi_select && !((_metadata$valid_value = metadata.valid_values) !== null && _metadata$valid_value !== void 0 && _metadata$valid_value.length)) {
      return "tag";
    }

    // Priority 3: Check if pattern suggests date (even if data_type is string)
    if (isDatePattern((_metadata$pattern = metadata.pattern) !== null && _metadata$pattern !== void 0 ? _metadata$pattern : undefined)) {
      return "date";
    }

    // Priority 4: Check if pattern suggests numeric (even if data_type is string)
    if (isNumericPattern((_metadata$pattern2 = metadata.pattern) !== null && _metadata$pattern2 !== void 0 ? _metadata$pattern2 : undefined)) {
      return "number";
    }

    // Priority 5: Check explicit data_type
    switch (dataType) {
      case "date":
      case "datetime":
        return "date";
      case "number":
      case "integer":
      case "float":
      case "decimal":
        return "number";
      case "string":
      case "text":
      default:
        return "text";
    }
  };
  var inputType = getInputType();
  var placeholder = inputType === "select" || inputType === "multi-select" ? "Select..." : inputType === "date" ? "Select date..." : "";
  var options = metadata.valid_values || [];
  var adjustedMaxLength = getAdjustedMaxLength(metadata.max_length, (_metadata$pattern3 = metadata.pattern) !== null && _metadata$pattern3 !== void 0 ? _metadata$pattern3 : "");
  var shouldAllowCustomValue = ["radiologist_reading_npi", "reconstructed_image_width"].includes(field.field_name.toLowerCase());

  // Apply LCSR validation rules to override null/incorrect API values
  var validatedRules = applyLCSRValidationRules(field.field_name, metadata.min_value, metadata.max_value, metadata.format_hint);
  switch (inputType) {
    case "tag":
      return /*#__PURE__*/React.createElement(TagInput, {
        value: value,
        placeholder: placeholder,
        onChange: onChange,
        formatHint: metadata.format_hint,
        pattern: metadata.pattern,
        maxLength: adjustedMaxLength,
        disabled: disabled,
        isEmpty: isEmpty,
        isRequired: isRequired,
        errorMessage: errorMessage,
        isShouldBeBlankError: isShouldBeBlankError
      });
    case "text":
      return /*#__PURE__*/React.createElement(TextInput, {
        value: value,
        placeholder: placeholder,
        onChange: onChange,
        pattern: metadata.pattern,
        formatHint: validatedRules.formatHint,
        maxLength: adjustedMaxLength,
        minValue: validatedRules.minValue,
        maxValue: validatedRules.maxValue,
        disabled: disabled,
        isEmpty: isEmpty,
        isRequired: isRequired,
        errorMessage: errorMessage,
        isShouldBeBlankError: isShouldBeBlankError
      });
    case "number":
      return /*#__PURE__*/React.createElement(NumberInput, {
        value: value,
        placeholder: placeholder,
        onChange: onChange,
        pattern: metadata.pattern,
        formatHint: validatedRules.formatHint,
        maxLength: adjustedMaxLength,
        minValue: validatedRules.minValue,
        maxValue: validatedRules.maxValue,
        disabled: disabled,
        isEmpty: isEmpty,
        isRequired: isRequired,
        errorMessage: errorMessage,
        isShouldBeBlankError: isShouldBeBlankError
      });
    case "date":
      return /*#__PURE__*/React.createElement(DateInput, {
        value: value,
        placeholder: placeholder,
        onChange: onChange,
        pattern: metadata.pattern,
        formatHint: metadata.format_hint,
        disabled: disabled,
        isEmpty: isEmpty,
        isRequired: isRequired,
        errorMessage: errorMessage,
        isShouldBeBlankError: isShouldBeBlankError,
        fieldName: field.field_name
      });
    case "select":
      return /*#__PURE__*/React.createElement(SelectInput, {
        value: value,
        placeholder: placeholder,
        onChange: onChange,
        options: options,
        onOpenChange: onDropdownStateChange,
        disabled: disabled,
        isEmpty: isEmpty,
        isRequired: isRequired,
        errorMessage: errorMessage,
        allowCustomValue: shouldAllowCustomValue,
        pattern: shouldAllowCustomValue ? metadata.pattern : undefined,
        formatHint: shouldAllowCustomValue ? metadata.format_hint : undefined,
        maxLength: shouldAllowCustomValue ? adjustedMaxLength : undefined,
        isShouldBeBlankError: isShouldBeBlankError
      });
    case "multi-select":
      return /*#__PURE__*/React.createElement(MultiSelectInput, {
        value: value,
        placeholder: placeholder,
        onChange: onChange,
        options: options,
        onOpenChange: onDropdownStateChange,
        disabled: disabled,
        isEmpty: isEmpty,
        isRequired: isRequired,
        errorMessage: errorMessage,
        isShouldBeBlankError: isShouldBeBlankError
      });
    case "badge-multi-select":
      return /*#__PURE__*/React.createElement(BadgeMultiSelectInput, {
        value: value,
        options: options,
        onChange: onChange,
        disabled: disabled,
        isEmpty: isEmpty,
        isRequired: isRequired,
        errorMessage: errorMessage,
        isShouldBeBlankError: isShouldBeBlankError,
        fieldName: field.field_name
      });
    default:
      return null;
  }
};
export default FieldInputRenderer;